import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Eye, Download, Send, Copy, XCircle, Mail, X,
  CheckCircle, Clock, AlertTriangle, BookOpen,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft:             "bg-gray-100 text-gray-700",
  sent:              "bg-blue-100 text-blue-800",
  accepted:          "bg-green-100 text-green-800",
  changes_requested: "bg-yellow-100 text-yellow-800",
  expired:           "bg-orange-100 text-orange-800",
  cancelled:         "bg-red-100 text-red-700",
};

const OCC_LABELS    = { single: "Single", double: "Double", triple: "Triple", quad: "Quad" };
const OCC_CAPACITY  = { single: 1, double: 2, triple: 3, quad: 4 };

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

function isExpired(validUntil) {
  if (!validUntil) return false;
  try { return new Date(validUntil) < new Date(); }
  catch { return false; }
}

function defaultValidUntil() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

export default function UmrahQuotationPanel({ pkg }) {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [msg, setMsg] = useState(null);

  // Create quotation dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    number_of_travellers: 1,
    selected_occupancy: "",
    price_per_person: "",
    total_price: "",
    quotation_date: new Date().toISOString().slice(0, 10),
    valid_until: defaultValidUntil(),
    notes: "",
  });

  // Send dialog
  const [sendDialog, setSendDialog] = useState(null); // quotation doc
  const [sendEmail, setSendEmail] = useState("");

  const load = useCallback(async () => {
    if (!pkg?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/umrah-quotations?package_id=${pkg.id}`, { headers: getAuthHeaders() });
      setQuotations(r.data || []);
    } catch { /**/ } finally {
      setLoading(false);
    }
  }, [pkg?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    return () => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const showMsg = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 5000);
  };

  // Which occupancy options are enabled on this package?
  const enabledOccupancies = ["single", "double", "triple", "quad"].filter(key => {
    const occ = pkg?.[`occupancy_${key}`];
    return occ?.enabled && occ?.selling_price;
  });

  // Auto-calculate total when occupancy or travellers change
  const handleFormChange = (field, value) => {
    setForm(f => {
      const updated = { ...f, [field]: value };
      if (field === "selected_occupancy" || field === "number_of_travellers") {
        const occKey = field === "selected_occupancy" ? value : updated.selected_occupancy;
        const occ = pkg?.[`occupancy_${occKey}`];
        if (occ?.selling_price) {
          updated.price_per_person = occ.selling_price;
          updated.total_price = occ.selling_price * parseInt(
            field === "number_of_travellers" ? value : updated.number_of_travellers
          );
        }
      }
      if (field === "price_per_person" || field === "number_of_travellers") {
        const ppp = parseFloat(field === "price_per_person" ? value : updated.price_per_person);
        const n = parseInt(field === "number_of_travellers" ? value : updated.number_of_travellers);
        if (!isNaN(ppp) && !isNaN(n)) updated.total_price = ppp * n;
      }
      return updated;
    });
  };

  const openCreate = () => {
    const firstEnabled = enabledOccupancies[0] || "";
    const occ = firstEnabled ? pkg?.[`occupancy_${firstEnabled}`] : null;
    setForm({
      customer_name: "",
      customer_email: "",
      customer_phone: "",
      number_of_travellers: 1,
      selected_occupancy: firstEnabled,
      price_per_person: occ?.selling_price || "",
      total_price: occ?.selling_price || "",
      quotation_date: new Date().toISOString().slice(0, 10),
      valid_until: defaultValidUntil(),
      notes: "",
    });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!form.customer_name.trim()) { showMsg("Customer name is required.", "error"); return; }
    if (!form.selected_occupancy) { showMsg("Please select an occupancy.", "error"); return; }
    setActionLoading("create");
    try {
      const resp = await axios.post(`${API}/umrah-quotations`, {
        package_id: pkg.id,
        customer_name: form.customer_name,
        customer_email: form.customer_email || null,
        customer_phone: form.customer_phone || null,
        quotation_date: form.quotation_date,
        valid_until: form.valid_until || null,
        selected_occupancy: form.selected_occupancy,
        number_of_travellers: parseInt(form.number_of_travellers) || 1,
        price_per_person: parseFloat(form.price_per_person) || 0,
        total_price: parseFloat(form.total_price) || 0,
        notes: form.notes || null,
      }, { headers: getAuthHeaders() });
      setCreateOpen(false);
      const warnings = resp.data?.pricing_warnings || [];
      if (warnings.length > 0) {
        showMsg(`Quotation created. ⚠ ${warnings[0]}`, "error");
      } else {
        showMsg("Quotation created.");
      }
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      showMsg(typeof d === "string" ? d : "Failed to create quotation.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const openPreview = async (q) => {
    setActionLoading(`preview-${q.id}`);
    try {
      const r = await axios.get(`${API}/umrah-quotations/${q.id}/preview-pdf`, {
        responseType: "blob", headers: getAuthHeaders(),
      });
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      setPreviewUrl(url);
    } catch { showMsg("Failed to load preview.", "error"); }
    finally { setActionLoading(null); }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const downloadPdf = async (q) => {
    setActionLoading(`dl-${q.id}`);
    try {
      const r = await axios.get(`${API}/umrah-quotations/${q.id}/download-pdf`, {
        responseType: "blob", headers: getAuthHeaders(),
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${q.quotation_number || q.id}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { showMsg("Failed to download.", "error"); }
    finally { setActionLoading(null); }
  };

  const openSendDialog = (q) => {
    setSendEmail(q.customer_email || "");
    setSendDialog(q);
  };

  const sendTestEmail = async (q) => {
    setActionLoading(`test-${q.id}`);
    try {
      const r = await axios.post(`${API}/umrah-quotations/${q.id}/send-email`, {
        is_test: true,
        recipient_email: "",
      }, { headers: getAuthHeaders() });
      showMsg(r.data.message || "Test email sent to admin.");
    } catch {
      showMsg("Failed to send test email.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const sendQuotation = async (isTest = false) => {
    if (!sendDialog) return;
    const quotId = sendDialog.id;
    setActionLoading(isTest ? `test-${quotId}` : `send-${quotId}`);
    setSendDialog(null);
    try {
      const r = await axios.post(`${API}/umrah-quotations/${quotId}/send-email`, {
        is_test: isTest,
        recipient_email: sendEmail,
      }, { headers: getAuthHeaders() });
      showMsg(r.data.message || (isTest ? "Test email sent to admin." : `Quotation sent to ${sendEmail}.`));
      if (!isTest) await load();
    } catch {
      showMsg("Failed to send email.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDuplicate = async (q) => {
    setActionLoading(`dup-${q.id}`);
    try {
      const r = await axios.post(`${API}/umrah-quotations/${q.id}/duplicate`, {}, { headers: getAuthHeaders() });
      showMsg(`Duplicated as ${r.data.quotation_number}`);
      await load();
    } catch { showMsg("Failed to duplicate.", "error"); }
    finally { setActionLoading(null); }
  };

  const handleConvertToBooking = async (q) => {
    if (q.booking_reference) {
      // Already converted — navigate to the existing booking
      const r = await axios.get(`${API}/umrah-bookings?search=${q.booking_reference}`, { headers: getAuthHeaders() });
      const existing = (r.data || []).find(b => b.booking_reference === q.booking_reference);
      if (existing) { navigate(`/umrah-bookings/${existing.id}`); return; }
    }
    if (q.status !== "accepted") {
      if (!window.confirm(`This quotation is "${q.status}". Only "accepted" quotations should normally be converted. Proceed anyway?`)) return;
    }
    setActionLoading(`book-${q.id}`);
    try {
      const r = await axios.post(`${API}/umrah-bookings`, { quotation_id: q.id, force: true }, { headers: getAuthHeaders() });
      const booking = r.data.booking;
      showMsg(r.data.already_existed ? `Booking ${booking.booking_reference} already exists.` : `Booking ${booking.booking_reference} created.`);
      await load();
      navigate(`/umrah-bookings/${booking.id}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      showMsg(typeof d === "string" ? d : "Failed to create booking.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (q) => {
    if (!window.confirm(`Cancel quotation ${q.quotation_number}?`)) return;
    setActionLoading(`cancel-${q.id}`);
    try {
      await axios.patch(`${API}/umrah-quotations/${q.id}/status`, { status: "cancelled" }, { headers: getAuthHeaders() });
      await load();
    } catch { showMsg("Failed to cancel.", "error"); }
    finally { setActionLoading(null); }
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300";
  const selectCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300";

  return (
    <div className="space-y-4">
      {msg && (
        <div className={`rounded-lg px-4 py-3 text-sm font-semibold ${msg.type === "error" ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
          {msg.text}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-sm font-bold text-amber-800 uppercase tracking-wider">Quotations</h3>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-4 py-2 text-sm font-semibold"
        >
          <Plus size={15} />
          Create Quotation
        </button>
      </div>

      {/* Quotations list */}
      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : quotations.length === 0 ? (
        <p className="text-sm text-gray-400">No quotations yet. Create the first one above.</p>
      ) : (
        <div className="space-y-2">
          {quotations.map(q => {
            const snap = q.snapshot || {};
            const expired = isExpired(q.valid_until);
            return (
              <div key={q.id} className="border border-gray-200 rounded-xl p-4 bg-white">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-bold text-amber-800">{q.quotation_number}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${STATUS_COLORS[q.status] || "bg-gray-100 text-gray-600"}`}>
                        {q.status?.replace("_", " ")}
                      </span>
                      {expired && q.status !== "expired" && q.status !== "cancelled" && (
                        <span className="flex items-center gap-1 text-xs text-orange-600 font-semibold">
                          <AlertTriangle size={11} /> EXPIRED
                        </span>
                      )}
                      {q.status === "sent" && (
                        <span className="flex items-center gap-1 text-xs text-blue-600">
                          <CheckCircle size={11} /> Sent {fmtDate(q.sent_at)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-gray-900 mt-1">{q.customer_name}</p>
                    {q.customer_email && <p className="text-xs text-gray-400">{q.customer_email}</p>}
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-xs text-gray-500">
                        {OCC_LABELS[q.selected_occupancy] || q.selected_occupancy}
                        {OCC_CAPACITY[q.selected_occupancy] && ` (${OCC_CAPACITY[q.selected_occupancy]}pp/room)`}
                        {" · "}{q.number_of_travellers} traveller{q.number_of_travellers !== 1 ? "s" : ""}
                        {OCC_CAPACITY[q.selected_occupancy] && ` · ${Math.ceil(q.number_of_travellers / OCC_CAPACITY[q.selected_occupancy])} room${Math.ceil(q.number_of_travellers / OCC_CAPACITY[q.selected_occupancy]) !== 1 ? "s" : ""}`}
                      </span>
                      <span className="text-xs font-semibold text-amber-800">€{(q.total_price || 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}</span>
                      {q.valid_until && (
                        <span className={`text-xs ${expired ? "text-orange-600 font-semibold" : "text-gray-400"}`}>
                          Valid until {fmtDate(q.valid_until)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 flex-wrap">
                    <button
                      title="Preview PDF"
                      disabled={!!actionLoading}
                      onClick={() => openPreview(q)}
                      className="flex items-center gap-1 text-xs border border-amber-300 hover:bg-amber-50 text-amber-700 rounded-lg px-2.5 py-1.5 font-semibold disabled:opacity-40"
                    >
                      <Eye size={12} />
                      {actionLoading === `preview-${q.id}` ? "…" : "Preview"}
                    </button>
                    <button
                      title="Download PDF"
                      disabled={!!actionLoading}
                      onClick={() => downloadPdf(q)}
                      className="flex items-center gap-1 text-xs border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg px-2.5 py-1.5 font-semibold disabled:opacity-40"
                    >
                      <Download size={12} />
                      {actionLoading === `dl-${q.id}` ? "…" : "Download"}
                    </button>
                    <button
                      title="Send to Customer"
                      disabled={!!actionLoading}
                      onClick={() => openSendDialog(q)}
                      className="flex items-center gap-1 text-xs border border-green-300 hover:bg-green-50 text-green-700 rounded-lg px-2.5 py-1.5 font-semibold disabled:opacity-40"
                    >
                      <Send size={12} />
                      Send
                    </button>
                    <button
                      title="Test Email (Admin Only)"
                      disabled={!!actionLoading}
                      onClick={() => sendTestEmail(q)}
                      className="flex items-center gap-1 text-xs border border-gray-200 hover:bg-gray-50 text-gray-500 rounded-lg px-2.5 py-1.5 font-semibold disabled:opacity-40"
                    >
                      <Mail size={12} />
                      {actionLoading === `test-${q.id}` ? "…" : "Test"}
                    </button>
                    <button
                      title="Duplicate"
                      disabled={!!actionLoading}
                      onClick={() => handleDuplicate(q)}
                      className="flex items-center gap-1 text-xs border border-gray-200 hover:bg-gray-50 text-gray-500 rounded-lg px-2.5 py-1.5 font-semibold disabled:opacity-40"
                    >
                      <Copy size={12} />
                      {actionLoading === `dup-${q.id}` ? "…" : "Duplicate"}
                    </button>
                    {q.status !== "cancelled" && (
                      <button
                        title="Cancel"
                        disabled={!!actionLoading}
                        onClick={() => handleCancel(q)}
                        className="flex items-center gap-1 text-xs border border-red-200 hover:bg-red-50 text-red-500 rounded-lg px-2.5 py-1.5 font-semibold disabled:opacity-40"
                      >
                        <XCircle size={12} />
                        {actionLoading === `cancel-${q.id}` ? "…" : "Cancel"}
                      </button>
                    )}
                    {q.status !== "cancelled" && (
                      <button
                        title={q.booking_reference ? `View booking ${q.booking_reference}` : "Convert to Booking"}
                        disabled={!!actionLoading}
                        onClick={() => handleConvertToBooking(q)}
                        className={`flex items-center gap-1 text-xs rounded-lg px-2.5 py-1.5 font-semibold disabled:opacity-40 ${
                          q.booking_reference
                            ? "border border-green-300 bg-green-50 text-green-700 hover:bg-green-100"
                            : "border border-amber-300 hover:bg-amber-50 text-amber-700"
                        }`}
                      >
                        <BookOpen size={12} />
                        {actionLoading === `book-${q.id}` ? "…" : q.booking_reference ? q.booking_reference : "Book"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* PDF Preview modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/60 flex flex-col z-50">
          <div className="flex items-center justify-between bg-white px-4 py-3 border-b border-gray-200 shrink-0">
            <span className="text-sm font-semibold text-gray-800">Umrah Quotation Preview</span>
            <button
              onClick={closePreview}
              className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors"
            >
              <X size={16} /> Close
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <iframe src={previewUrl} title="Quotation Preview" className="w-full h-full border-0" />
          </div>
        </div>
      )}

      {/* Create Quotation dialog */}
      {createOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">Create Quotation</h3>
              <button onClick={() => setCreateOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Customer Name *</label>
                <input className={`mt-1 ${inputCls}`} value={form.customer_name} onChange={e => handleFormChange("customer_name", e.target.value)} placeholder="Full name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Customer Email</label>
                  <input type="email" className={`mt-1 ${inputCls}`} value={form.customer_email} onChange={e => handleFormChange("customer_email", e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Customer Phone</label>
                  <input className={`mt-1 ${inputCls}`} value={form.customer_phone} onChange={e => handleFormChange("customer_phone", e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Occupancy *</label>
                  <select className={`mt-1 ${selectCls}`} value={form.selected_occupancy} onChange={e => handleFormChange("selected_occupancy", e.target.value)}>
                    <option value="">— Select —</option>
                    {enabledOccupancies.map(key => {
                      const occ = pkg?.[`occupancy_${key}`];
                      const cap = OCC_CAPACITY[key];
                      return (
                        <option key={key} value={key}>
                          {OCC_LABELS[key]} ({cap}pp/room) — €{occ?.selling_price?.toLocaleString("en-GB", { minimumFractionDigits: 0 })} /pp
                        </option>
                      );
                    })}
                  </select>
                  {form.selected_occupancy && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {OCC_CAPACITY[form.selected_occupancy]} person{OCC_CAPACITY[form.selected_occupancy] > 1 ? "s" : ""} per room
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Number of Travellers</label>
                  <input type="number" min="1" className={`mt-1 ${inputCls}`} value={form.number_of_travellers} onChange={e => handleFormChange("number_of_travellers", e.target.value)} />
                  {form.selected_occupancy && form.number_of_travellers && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Rooms required: {Math.ceil(parseInt(form.number_of_travellers) / OCC_CAPACITY[form.selected_occupancy])}
                    </p>
                  )}
                </div>
              </div>

              {/* Live pricing calculation */}
              {form.selected_occupancy && form.price_per_person && form.number_of_travellers && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-xs text-gray-600">
                      <span className="font-semibold">{parseInt(form.number_of_travellers)} traveller{parseInt(form.number_of_travellers) !== 1 ? "s" : ""}</span>
                      {" × "}
                      <span className="font-semibold">€{parseFloat(form.price_per_person).toLocaleString("en-GB", { minimumFractionDigits: 0 })}/pp</span>
                      {" · "}
                      <span>{Math.ceil(parseInt(form.number_of_travellers) / OCC_CAPACITY[form.selected_occupancy])} room{Math.ceil(parseInt(form.number_of_travellers) / OCC_CAPACITY[form.selected_occupancy]) !== 1 ? "s" : ""} ({OCC_LABELS[form.selected_occupancy]})</span>
                    </div>
                    <div className="text-base font-bold text-amber-800">
                      €{parseFloat(form.total_price || 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Price / Person (€)</label>
                  <input type="number" step="0.01" className={`mt-1 ${inputCls}`} value={form.price_per_person} onChange={e => handleFormChange("price_per_person", e.target.value)} />
                  {form.selected_occupancy && pkg?.[`occupancy_${form.selected_occupancy}`]?.selling_price && (
                    <p className="text-xs text-gray-400 mt-0.5">Package price: €{pkg[`occupancy_${form.selected_occupancy}`].selling_price.toLocaleString()}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Total Price (€)</label>
                  <div className={`mt-1 ${inputCls} bg-amber-50 font-bold text-amber-800 text-base`}>
                    €{parseFloat(form.total_price || 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Quotation Date</label>
                  <input type="date" className={`mt-1 ${inputCls}`} value={form.quotation_date} onChange={e => handleFormChange("quotation_date", e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Valid Until</label>
                  <input type="date" className={`mt-1 ${inputCls}`} value={form.valid_until} onChange={e => handleFormChange("valid_until", e.target.value)} />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Additional Notes (shown on PDF)</label>
                <textarea rows={2} className={`mt-1 ${inputCls}`} value={form.notes} onChange={e => handleFormChange("notes", e.target.value)} placeholder="Optional notes for the customer…" />
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setCreateOpen(false)} className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2">Cancel</button>
              <button
                onClick={handleCreate}
                disabled={actionLoading === "create"}
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-5 py-2 text-sm font-semibold disabled:opacity-50"
              >
                {actionLoading === "create" ? "Creating…" : "Create Quotation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send dialog */}
      {sendDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Send Quotation</h3>
              <button onClick={() => setSendDialog(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              The quotation PDF will be generated and attached. Customer data at time of quotation creation is used.
            </p>
            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Recipient Email</label>
            <input
              type="email"
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-5"
              value={sendEmail}
              onChange={e => setSendEmail(e.target.value)}
            />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setSendDialog(null)} className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2">Cancel</button>
              <button
                onClick={() => sendQuotation(false)}
                disabled={!sendEmail || !!actionLoading}
                className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-5 py-2 text-sm font-semibold disabled:opacity-50"
              >
                {actionLoading === `send-${sendDialog?.id}` ? "Sending…" : "Send Quotation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
